import { DEV_USERS } from '@elytracms/core/persistence'
import type { AuthAdapter, AuthSession, SignInCredentials, SignInResult } from './auth-adapter'

/**
 * Deterministic local auth (EC-150) for the in-memory persistence mode: the
 * whole sign-in → role-enforcement → sign-out flow works offline, in tests,
 * and in fixtures with zero backend setup. User ids are the canonical
 * `DEV_USERS` ids from `@elytracms/core/persistence`, which the seeded
 * `projectMembers` fixtures reference — so `admin@example.com` is admin,
 * `editor@…` editor, `viewer@…` viewer on every sample project.
 *
 * Dev credentials (fixed, documented, local-only):
 * - admin@example.com  / elytra-admin
 * - editor@example.com / elytra-editor
 * - viewer@example.com / elytra-viewer
 *
 * Adminness is the `projectMembers` role, not a global flag — project creation
 * is config/CLI-owned now (AD-11), so there is no in-studio `create-projects`
 * capability to grant.
 *
 * The session survives reloads via injected storage (defaults to
 * `sessionStorage` in the browser, an in-memory map elsewhere).
 */

export const LOCAL_DEV_CREDENTIALS = [
  { user: DEV_USERS.admin, password: 'elytra-admin' },
  { user: DEV_USERS.editor, password: 'elytra-editor' },
  { user: DEV_USERS.viewer, password: 'elytra-viewer' },
] as const

const STORAGE_KEY = 'elytra.local-auth.user-id'

/** The storage slice the adapter needs (sessionStorage-compatible). */
export interface SessionStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function defaultStore(): SessionStore {
  if (typeof window !== 'undefined' && window.sessionStorage) return window.sessionStorage
  const memory = new Map<string, string>()
  return {
    getItem: (key) => memory.get(key) ?? null,
    setItem: (key, value) => void memory.set(key, value),
    removeItem: (key) => void memory.delete(key),
  }
}

export class LocalAuthAdapter implements AuthAdapter {
  private readonly store: SessionStore

  constructor(store: SessionStore = defaultStore()) {
    this.store = store
  }

  async currentSession(): Promise<AuthSession | null> {
    const userId = this.store.getItem(STORAGE_KEY)
    if (!userId) return null
    const entry = LOCAL_DEV_CREDENTIALS.find((c) => c.user.id === userId)
    return entry ? { user: entry.user } : null
  }

  async signIn(credentials: SignInCredentials): Promise<SignInResult> {
    const email = credentials.email.trim().toLowerCase()
    const entry = LOCAL_DEV_CREDENTIALS.find((c) => c.user.email === email)
    if (!entry || entry.password !== credentials.password) {
      // Deliberately identical for unknown email vs wrong password.
      return {
        ok: false,
        reason: 'invalid-credentials',
        message: 'Email or password is incorrect.',
      }
    }
    this.store.setItem(STORAGE_KEY, entry.user.id)
    return { ok: true, session: { user: entry.user } }
  }

  async signUp(): Promise<SignInResult> {
    // Playground mode has a fixed, documented set of dev users — there is no
    // registration. Surfaced as a state, not a throw, so the sign-in screen can
    // explain it.
    return {
      ok: false,
      reason: 'unavailable',
      message: 'Sign-up isn’t available in playground mode — use the dev credentials below.',
    }
  }

  async signOut(): Promise<void> {
    this.store.removeItem(STORAGE_KEY)
  }
}
